import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import useGetQueryFn from '../use-get-query-fn';
import type {
  AdminCatalogApp,
  AppCatalogSection,
  AppsCatalogAppsParams,
  AppsCatalogMeta,
  AppsCatalogPreview,
  AppsCatalogPreviewParams,
} from '@/client/types/apps-catalog';

export const APPS_CATALOG_BASE = '/api/admin/apps-catalog';

export const appsCatalogKeys = {
  all: ['apps-catalog-admin'] as const,
  meta: () => [...appsCatalogKeys.all, 'meta'] as const,
  sections: () => [...appsCatalogKeys.all, 'sections'] as const,
  apps: (params: AppsCatalogAppsParams = {}) => [...appsCatalogKeys.all, 'apps', params] as const,
  app: (appKey: string) => [...appsCatalogKeys.all, 'app', appKey] as const,
  preview: (params: AppsCatalogPreviewParams) =>
    [...appsCatalogKeys.all, 'preview', params] as const,
};

/** ff-api wraps payloads as `{ status, data }`. */
export const unwrapEnvelope = (raw: unknown): any =>
  raw != null && typeof raw === 'object' && 'status' in (raw as object) && 'data' in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const EMPTY_META: AppsCatalogMeta = {
  sites: [],
  statuses: [],
  marketing_labels: [],
  icons: [],
  known_app_keys: [],
  permissions: {
    read: 'apps.catalog.read',
    write: 'apps.catalog.write',
    publish: 'apps.catalog.publish',
  },
};

// ─── Meta ────────────────────────────────────────────────────────────────────

export const useAppsCatalogMetaQuery = (enabled = true) => {
  const queryFn = useGetQueryFn<unknown>({
    endpoint: `${APPS_CATALOG_BASE}/meta`,
    defaultErrorMessage: 'Failed to load the apps catalog vocabulary',
  });
  return useQuery({
    queryKey: appsCatalogKeys.meta(),
    queryFn: async (): Promise<AppsCatalogMeta> => {
      const payload = unwrapEnvelope(await queryFn());
      return { ...EMPTY_META, ...(payload ?? {}) };
    },
    enabled,
    // Closed sets change only with an ff-api deploy.
    staleTime: DEFAULT_STALE_TIME * 6,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Sections ────────────────────────────────────────────────────────────────

export const useAppsCatalogSectionsQuery = (enabled = true) => {
  const queryFn = useGetQueryFn<unknown>({
    endpoint: `${APPS_CATALOG_BASE}/sections`,
    defaultErrorMessage: 'Failed to load catalog sections',
  });
  return useQuery({
    queryKey: appsCatalogKeys.sections(),
    queryFn: async (): Promise<AppCatalogSection[]> => {
      const payload = unwrapEnvelope(await queryFn());
      return Array.isArray(payload?.sections) ? payload.sections : [];
    },
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Apps ────────────────────────────────────────────────────────────────────

export const useAppsCatalogAppsQuery = (params: AppsCatalogAppsParams = {}, enabled = true) => {
  const queryFn = useGetQueryFn<unknown>({
    endpoint: `${APPS_CATALOG_BASE}/apps`,
    defaultErrorMessage: 'Failed to load catalog apps',
  });
  return useQuery({
    queryKey: appsCatalogKeys.apps(params),
    queryFn: async (): Promise<AdminCatalogApp[]> => {
      const payload = unwrapEnvelope(await queryFn({ ...params }));
      return Array.isArray(payload?.apps) ? payload.apps : [];
    },
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useAppsCatalogAppQuery = (appKey: string | null | undefined, enabled = true) => {
  const queryFn = useGetQueryFn<unknown>({
    endpoint: `${APPS_CATALOG_BASE}/apps/${encodeURIComponent(appKey ?? '')}`,
    defaultErrorMessage: 'Failed to load the app',
  });
  return useQuery({
    queryKey: appsCatalogKeys.app(appKey ?? ''),
    queryFn: async (): Promise<AdminCatalogApp | null> => {
      const payload = unwrapEnvelope(await queryFn());
      return payload?.app ?? null;
    },
    enabled: enabled && !!appKey,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    // A 404 is an answer, not a blip.
    retry: (count, error: any) => error?.status !== 404 && count < 2,
  });
};

// ─── Preview ─────────────────────────────────────────────────────────────────

export const useAppsCatalogPreviewQuery = (params: AppsCatalogPreviewParams, enabled = true) => {
  const queryFn = useGetQueryFn<unknown>({
    endpoint: `${APPS_CATALOG_BASE}/preview`,
    defaultErrorMessage: 'Failed to load the catalog preview',
  });
  return useQuery({
    queryKey: appsCatalogKeys.preview(params),
    queryFn: async (): Promise<AppsCatalogPreview> => {
      const payload = unwrapEnvelope(
        await queryFn({
          site: params.site,
          surface: params.surface,
          // Omit false flags rather than sending "false".
          include_unpublished: params.include_unpublished ? 'true' : undefined,
          include_hidden: params.include_hidden ? 'true' : undefined,
        }),
      );
      return {
        site: payload?.site ?? params.site,
        surface: payload?.surface ?? params.surface,
        sections: Array.isArray(payload?.sections) ? payload.sections : [],
      };
    },
    enabled,
    staleTime: 0,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
